import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Camera, Check, CheckCheck, Loader2, Plus, X, Eye, Sparkles,
  Settings, Trash2, AlertTriangle, ChevronRight, ChevronDown, ArrowLeft, ListChecks,
  Package, Image as ImageIcon, Wrench, Ruler, Cpu, FileText, Copy, Building2, PencilLine,
} from "lucide-react";
import { toast } from "sonner";
import MultiCaptureSheet from "@/components/MultiCaptureSheet";
import FastCaptureSheet from "@/components/FastCaptureSheet";
import FinalizeReviewDialog from "@/components/FinalizeReviewDialog";
import MoveToFolderDialog from "@/components/MoveToFolderDialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

/**
 * Installer edit view — accessed via /r-edit/{edit_token}. No auth.
 * The token grants permission to fill in unit-level fields only:
 * status, description, observations, follow-up actions, checklist state,
 * photos, materials used. Top-level project info (customer, address, etc.)
 * remains read-only here. Installer can also add/remove units, preview the
 * customer-facing PDF, and trigger the AI summary.
 */
export default function PublicReportEdit() {
  const { editToken, unitId, section } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savedMaterials, setSavedMaterials] = useState([]);
  const [manageOpen, setManageOpen] = useState(false);
  const [summaryBusy, setSummaryBusy] = useState(false);
  const [photoEdit, setPhotoEdit] = useState(null); // { photo, unitId } | null
  // Multi-capture sheet for "Valitse galleriasta" + camera-fallback —
  // null when closed, { unit, source } when open
  const [multiCaptureUnit, setMultiCaptureUnit] = useState(null);
  // Fast Capture (live-camera via getUserMedia) — null when closed, { unit } when open.
  const [fastCaptureUnit, setFastCaptureUnit] = useState(null);

  // "Ota useita kuvia" → Fast Capture v1 (Veraso-kamera, getUserMedia).
  // Fallback: MultiCaptureSheet (file-input camera) if browser lacks getUserMedia.
  // iOS Safari: getUserMedia kysyy kameraluvan lähes joka kerta (Safari ei
  // muista lupaa istuntojen välillä yhtä hyvin kuin Android Chrome). Käytämme
  // iOS Safarilla suoraan käyttöjärjestelmän kameraa MultiCaptureSheet-kautta
  // — hieman hitaampi flow, mutta ei häiritseviä lupakyselyitä.
  // `opts.single` = true → kun Valmis painetaan, käyttäjä siirtyy Photos-näkymään
  // ja captionit näkyvät inline. (sama UX kuin multi-mode; "Ota kuva" on vain
  // semanttinen vaihtoehto käyttäjälle joka olettaa "yksi kuva kerrallaan".)
  const openMultiCapture = (unitForCapture, opts = {}) => {
    const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
    // iOS Safari (iPhone/iPad/iPod, ei Chrome/Firefox iOS:llä joissa on eri UA).
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isIOSSafari = isIOS && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    if (!isIOSSafari && typeof navigator !== "undefined" && navigator.mediaDevices?.getUserMedia) {
      setFastCaptureUnit({ unit: unitForCapture, single: !!opts.single });
    } else {
      setMultiCaptureUnit({ unit: unitForCapture, source: "camera", single: !!opts.single });
    }
  };
  // Last-saved timestamp + tick to keep the "x sek sitten" label live.
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [, setTick] = useState(0);

  // Remember the Level-1 (unit list) scroll position across drill-downs so
  // that returning from a unit does not snap the list back to the top.
  const level1ScrollRef = useRef(0);

  useEffect(() => {
    if (!editToken) return;
    setLoading(true);
    axios.get(`${API}/reports/edit/${editToken}`)
      .then(({ data }) => setReport(data))
      .catch((e) => setError(e?.response?.data?.detail || "Linkki ei kelpaa"))
      .finally(() => setLoading(false));
    // Saved materials for quick-pick pills (best-effort)
    axios.get(`${API}/reports/edit/${editToken}/company-materials`)
      .then(({ data }) => setSavedMaterials(data?.materials || []))
      .catch(() => {});
  }, [editToken]);

  // Re-render once per 15s so "x sek/min sitten" stays accurate without
  // listening to a server stream. Cheap on mobile.
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 15000);
    return () => clearInterval(id);
  }, []);

  // Scroll behavior between levels:
  //   • Entering Level 2 or Level 3 (drilling deeper) → scroll to top so the
  //     new view starts at its own top (previously it inherited the parent's
  //     scroll — e.g. opening "Asunto 27" left the page scrolled far down
  //     and the Kuvat section was below the fold).
  //   • Returning to Level 1 (back from a unit) → restore the previously
  //     saved unit-list scroll so the installer sees the unit they just came
  //     from without hunting for it.
  const prevLevelRef = useRef(null);
  useEffect(() => {
    const currentLevel = !unitId ? 1 : !section ? 2 : 3;
    const prev = prevLevelRef.current;
    prevLevelRef.current = currentLevel;

    if (currentLevel === 1) {
      // Only restore if we are actually returning from a deeper level (not
      // on the first mount) AND we have a saved position.
      if (prev === null) return;
      const y = level1ScrollRef.current;
      if (!y) {
        window.scrollTo(0, 0);
        return;
      }
      const raf1 = requestAnimationFrame(() => {
        const raf2 = requestAnimationFrame(() => window.scrollTo(0, y));
        level1ScrollRef._raf2 = raf2;
      });
      return () => {
        cancelAnimationFrame(raf1);
        if (level1ScrollRef._raf2) cancelAnimationFrame(level1ScrollRef._raf2);
      };
    }

    // Level 2 or 3 — always start at the top on a level transition. Skip
    // when the level did not actually change (e.g. child re-render).
    if (prev !== currentLevel) {
      window.scrollTo(0, 0);
    }
  }, [unitId, section]);

  const markSaved = () => setLastSavedAt(Date.now());

  const patchUnit = async (unitId, patch) => {
    try {
      const { data } = await axios.patch(`${API}/reports/edit/${editToken}/units/${unitId}`, patch);
      setReport(data);
      markSaved();
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tallennus epäonnistui");
      throw e;
    }
  };

  const patchInfo = async (patch) => {
    try {
      const { data } = await axios.patch(`${API}/reports/edit/${editToken}/info`, patch);
      setReport(data);
      markSaved();
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tallennus epäonnistui");
      throw e;
    }
  };

  const addUnit = async (label) => {
    const trimmed = (label || "").trim();
    if (!trimmed) return;
    try {
      const { data } = await axios.post(`${API}/reports/edit/${editToken}/units`, {
        label: trimmed, description: "",
      });
      setReport(data);
      markSaved();
      toast.success("Yksikkö lisätty");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lisäys epäonnistui");
    }
  };

  const deleteUnit = async (unitId) => {
    if (!window.confirm("Poistetaanko yksikkö? Tämä poistaa myös kaikki sen kuvat.")) return;
    try {
      const { data } = await axios.delete(`${API}/reports/edit/${editToken}/units/${unitId}`);
      setReport(data);
      markSaved();
      toast.success("Yksikkö poistettu");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Poisto epäonnistui");
    }
  };

  const renameUnit = async (unitId, newLabel) => {
    const trimmed = (newLabel || "").trim();
    if (!trimmed) return;
    try {
      await patchUnit(unitId, { label: trimmed });
      toast.success("Nimi päivitetty");
    } catch { /* patchUnit näyttää oman toastin */ }
  };

  const openPreview = () => {
    if (!report?.share_token) {
      toast.error("Esikatselu ei käytettävissä");
      return;
    }
    // iOS Safari / PWA does not reliably open a new tab via window.open — the
    // preview would trap the user and force them to quit the app. Instead we
    // navigate in-place and expose a "Takaisin muokkaukseen" link on the
    // public preview page (see PublicReport).
    try {
      window.sessionStorage.setItem("veraso_preview_back", `/r-edit/${editToken}`);
    } catch { /* ignore */ }
    navigate(`/r/${report.share_token}`);
  };

  const [confirmSummaryRegen, setConfirmSummaryRegen] = useState(false);

  const runGenerateSummary = async () => {
    setSummaryBusy(true);
    try {
      await axios.post(`${API}/reports/edit/${editToken}/summary`);
      toast.success("Yhteenveto luotu — näkyy raportissa");
      const { data } = await axios.get(`${API}/reports/edit/${editToken}`);
      setReport(data);
      markSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yhteenvedon luonti epäonnistui");
    } finally {
      setSummaryBusy(false);
    }
  };

  const generateSummary = async () => {
    // If a summary already exists, confirm before overwriting any field-worker edits.
    if ((report?.ai_summary || "").trim()) {
      setConfirmSummaryRegen(true);
      return;
    }
    await runGenerateSummary();
  };

  const openCameraForUnit = (unit) => {
    if (!unit) return;
    // Double-tap / swipe-left on a unit row: open Veraso live camera directly
    // for that unit (same flow as "Ota kuva" / "Ota useita kuvia").
    openMultiCapture(unit, { single: false });
  };

  // "Merkitse koko kohde valmiiksi" avaa nyt "Raportin viimeistely" -näkymän,
  // jossa työntekijä näkee koosteen (havainnot + tarvikkeet), kirjoittaa
  // koko kohteen yhteenvedon ja voi allekirjoittaa raportin. Vasta "Lähetä
  // raportti" -painike merkkaa yksiköt valmiiksi. AI:ta ei käytetä.
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  // "Siirrä kohteeseen" -toiminto vaatii kirjautumisen (owner tai member),
  // koska /api/reports/{id} PATCH käyttää yritys-scopea. Ulkoiset asentajat
  // linkin kautta eivät saa nähdä tätä toimintoa.
  const isAuthed = typeof window !== "undefined" && !!window.localStorage.getItem("veraso_token");

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }
  if (error || !report) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="bg-white rounded-2xl border border-slate-100 p-8 max-w-md text-center">
          <h1 className="font-display text-xl font-semibold text-slate-900">Linkki ei kelpaa</h1>
          <p className="text-sm text-slate-500 mt-2">{error || "Linkin omistaja on poistanut tai päivittänyt jakolinkin."}</p>
        </div>
      </div>
    );
  }

  const units = report.units || [];

  // -- Drill-down navigation --
  const currentUnit = unitId ? units.find((u) => u.id === unitId) : null;
  // If unitId points to a deleted/missing unit, bounce back to Level 1.
  // Wrapped in setTimeout to avoid setState-in-render.
  if (unitId && !currentUnit) {
    setTimeout(() => navigate(`/r-edit/${editToken}`, { replace: true }), 0);
  }
  const VALID_SECTIONS = ["photos", "work", "materials", "measurements", "devices", "custom"];
  const validSection = section && VALID_SECTIONS.includes(section) ? section : null;
  if (section && !validSection && currentUnit) {
    setTimeout(() => navigate(`/r-edit/${editToken}/unit/${unitId}`, { replace: true }), 0);
  }
  const level = !currentUnit ? 1 : !validSection ? 2 : 3;

  const goLevel1 = () => navigate(`/r-edit/${editToken}`);
  const goLevel2 = (uid) => {
    // Preserve the unit-list scroll position so returning to Level 1 does not
    // jump back to the top after the installer has scrolled down and opened a
    // unit. Only save when actually leaving Level 1.
    if (level === 1) level1ScrollRef.current = window.scrollY || window.pageYOffset || 0;
    navigate(`/r-edit/${editToken}/unit/${uid}`);
  };
  const goLevel3 = (uid, sec) => navigate(`/r-edit/${editToken}/unit/${uid}/${sec}`);

  // Quick-pick material pills combine BOTH report-level (wizard) materials and
  // company-level saved materials. Without this merge, materials added during
  // the wizard would never appear as quick-picks after report creation.
  const savedMaterialsMerged = (() => {
    const out = [];
    const seen = new Set();
    for (const m of (report?.materials || [])) {
      const name = (m?.name || "").trim();
      if (!name) continue;
      const k = name.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      out.push({ name, unit: (m?.unit || "kpl") });
    }
    for (const m of (savedMaterials || [])) {
      const name = (m?.name || "").trim();
      if (!name) continue;
      const k = name.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      out.push({ name, unit: (m?.unit || "kpl") });
    }
    return out;
  })();

  const onUnitPatch = (patch) => currentUnit && patchUnit(currentUnit.id, patch);

  return (
    <div className="min-h-screen bg-slate-50">
      {level === 1 && (
        <Level1Header
          report={report}
          lastSavedAt={lastSavedAt}
          onPreview={openPreview}
          onPatchInfo={patchInfo}
          onMove={isAuthed && report.report_id ? () => setMoveOpen(true) : null}
        />
      )}
      {level === 2 && currentUnit && (
        <DrillHeader
          eyebrow="Yksikkö"
          title={currentUnit.label}
          onBack={goLevel1}
          backLabel="Yksiköt"
          right={<UnitStatusPill status={currentUnit.status} />}
          lastSavedAt={lastSavedAt}
        />
      )}
      {level === 3 && currentUnit && (
        <DrillHeader
          eyebrow={currentUnit.label}
          title={SECTION_TITLES[validSection]}
          onBack={() => goLevel2(currentUnit.id)}
          backLabel={currentUnit.label}
          lastSavedAt={lastSavedAt}
        />
      )}

      <main className="max-w-3xl mx-auto px-4 py-5 pb-32">
        {level === 1 && (
          <Level1UnitList
            report={report}
            units={units}
            editToken={editToken}
            onOpenUnit={(u) => goLevel2(u.id)}
            onOpenCamera={openCameraForUnit}
            onManageUnits={() => setManageOpen(true)}
            onMarkAllDone={() => setFinalizeOpen(true)}
            onSummarySaved={(updated) => { setReport(updated); markSaved(); }}
            onPatchInfo={patchInfo}
          />
        )}
        {level === 2 && currentUnit && (
          <Level2UnitHub
            unit={currentUnit}
            report={report}
            onOpenSection={(sec) => goLevel3(currentUnit.id, sec)}
            onOpenSingle={() => openMultiCapture(currentUnit, { single: true })}
            onOpenGallery={() => setMultiCaptureUnit({ unit: currentUnit, source: "gallery" })}
          />
        )}
        {level === 3 && currentUnit && validSection === "photos" && (
          <Level3Photos
            unit={currentUnit}
            editToken={editToken}
            onOpenPhoto={(p) => setPhotoEdit({ photo: p, unitId: currentUnit.id })}
            onOpenCamera={() => openMultiCapture(currentUnit, { single: true })}
            onPhotoSaved={(updatedReport) => {
              if (updatedReport) setReport(updatedReport);
              markSaved();
            }}
          />
        )}
        {level === 3 && currentUnit && validSection === "work" && (
          <Level3Work unit={currentUnit} onPatch={onUnitPatch} />
        )}
        {level === 3 && currentUnit && validSection === "materials" && (
          <Level3Materials
            unit={currentUnit}
            savedMaterials={savedMaterialsMerged}
            onPatch={onUnitPatch}
          />
        )}
        {level === 3 && currentUnit && validSection === "measurements" && (
          <Level3Measurements unit={currentUnit} onPatch={onUnitPatch} />
        )}
        {level === 3 && currentUnit && validSection === "devices" && (
          <Level3Devices unit={currentUnit} onPatch={onUnitPatch} />
        )}
        {level === 3 && currentUnit && validSection === "custom" && (
          <Level3CustomFields unit={currentUnit} report={report} onPatch={onUnitPatch} />
        )}

        <footer className="mt-12 text-center text-xs text-slate-400">
          Tehty <a href="/" className="text-emerald-600 hover:underline">Verasolla</a>
        </footer>
      </main>

      {manageOpen && (
        <ManageDialog
          units={units}
          onClose={() => setManageOpen(false)}
          onAdd={addUnit}
          onDelete={deleteUnit}
          onRename={renameUnit}
        />
      )}

      {photoEdit && (
        <PhotoEditDialog
          photo={photoEdit.photo}
          editToken={editToken}
          onClose={() => setPhotoEdit(null)}
          onSaved={(updatedReport) => {
            if (updatedReport) setReport(updatedReport);
            markSaved();
          }}
        />
      )}

      {/* PhotoCaptureSheet mount poistettu — käyttäjäpyynnön mukaan is_finding
          (havainto-flag per kuva) ei ole enää käytössä. Kaikki kameranavigointi
          menee openMultiCapture:n kautta FastCaptureSheet:ään tai galleria-flow:hun. */}

      {multiCaptureUnit && (
        <MultiCaptureSheet
          unit={multiCaptureUnit.unit}
          source={multiCaptureUnit.source || "camera"}
          editToken={editToken}
          onClose={() => setMultiCaptureUnit(null)}
          onCompleted={(updatedReport, okCount) => {
            if (updatedReport) setReport(updatedReport);
            markSaved();
            if (okCount && okCount > 0) {
              goLevel3(multiCaptureUnit.unit.id, "photos");
            }
          }}
        />
      )}

      {fastCaptureUnit && (
        <FastCaptureSheet
          unit={fastCaptureUnit.unit}
          editToken={editToken}
          onClose={() => setFastCaptureUnit(null)}
          onCompleted={(updatedReport, okCount) => {
            if (updatedReport) setReport(updatedReport);
            markSaved();
            // Auto-navigate to the Photos sub-view so the user sees their photos
            // immediately and can edit captions inline (per user spec — no extra clicks).
            if (okCount && okCount > 0) {
              goLevel3(fastCaptureUnit.unit.id, "photos");
            }
          }}
          onFallback={() => {
            // User denied permission or no camera — switch to file-input flow
            // (propagate single-mode flag so 'Ota kuva' doesn't collapse to multi)
            setMultiCaptureUnit({ unit: fastCaptureUnit.unit, source: "camera", single: fastCaptureUnit.single });
          }}
        />
      )}

      {finalizeOpen && (
        <FinalizeReviewDialog
          report={report}
          editToken={editToken}
          onClose={() => setFinalizeOpen(false)}
          onCompleted={async () => {
            setFinalizeOpen(false);
            // Refresh report so updated unit statuses + summary are visible
            try {
              const { data } = await axios.get(`${API}/reports/edit/${editToken}`);
              setReport(data);
              markSaved();
            } catch {
              // best-effort
            }
          }}
        />
      )}

      {confirmSummaryRegen && (
        <ConfirmSummaryRegenDialog
          onCancel={() => setConfirmSummaryRegen(false)}
          onConfirm={async () => {
            setConfirmSummaryRegen(false);
            await runGenerateSummary();
          }}
        />
      )}

      {moveOpen && report.report_id && (
        <MoveToFolderDialog
          kind="report"
          itemId={report.report_id}
          currentPropertyId={report.property_id}
          onClose={() => setMoveOpen(false)}
          onMoved={async (newPid) => {
            // Refresh report to reflect new property_id in the header/state
            try {
              const { data } = await axios.get(`${API}/reports/edit/${editToken}`);
              setReport(data);
            } catch { /* best-effort */ }
            setMoveOpen(false);
            if (newPid) markSaved();
          }}
        />
      )}
    </div>
  );
}

/**
 * ConfirmSummaryRegenDialog — small modal asking the field worker to confirm
 * that they want to regenerate the AI summary (overwrites their manual edits).
 */
function ConfirmSummaryRegenDialog({ onCancel, onConfirm }) {
  return (
    <div
      className="fixed inset-0 z-[70] bg-black/50 flex items-end md:items-center justify-center p-0 md:p-4"
      onClick={onCancel}
      data-testid="edit-confirm-summary-regen"
    >
      <div
        className="bg-white rounded-t-2xl md:rounded-2xl shadow-xl w-full max-w-md p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display font-semibold text-slate-900 text-base">Luodaanko yhteenveto uudelleen?</h2>
            <p className="text-sm text-slate-600 mt-1 leading-relaxed">
              Nykyinen yhteenveto ja kaikki siihen tehdyt muokkaukset korvataan uudella AI-luonnoksella. Tätä ei voi peruuttaa.
            </p>
          </div>
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg border border-slate-200 text-slate-700 font-semibold text-sm hover:bg-slate-50"
            data-testid="edit-confirm-summary-cancel"
          >
            Peruuta
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-semibold text-sm"
            data-testid="edit-confirm-summary-confirm"
          >
            Kyllä, luo uudelleen
          </button>
        </div>
      </div>
    </div>
  );
}

const SECTION_TITLES = {
  photos: "Kuvat",
  work: "Tehdyt toimenpiteet / Lisätiedot",
  materials: "Tarvikkeet",
  measurements: "Mittaustulokset",
  devices: "Laitetiedot",
  custom: "Muut tiedot",
};

function Row({ label, v }) {
  return (
    <div>
      <span className="text-slate-500">{label}: </span>
      <span className="font-medium text-slate-800">{v}</span>
    </div>
  );
}

function SavedIndicator({ at }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!at) return;
    // Refresh the age label + drop the pulse class exactly at 4s.
    const t = setTimeout(() => setNow(Date.now()), 4000);
    return () => clearTimeout(t);
  }, [at]);
  if (!at) {
    return (
      <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-500 text-[11px] font-medium px-2.5 py-1 whitespace-nowrap" data-testid="edit-saved-indicator">
        Ei tallennuksia
      </span>
    );
  }
  const seconds = Math.max(1, Math.round((now - at) / 1000));
  const veryFresh = seconds < 4; // pulse for the first few seconds after save
  let label;
  if (seconds < 60) label = `${seconds} s sitten`;
  else if (seconds < 3600) label = `${Math.round(seconds / 60)} min sitten`;
  else label = `${Math.round(seconds / 3600)} h sitten`;
  return (
    <span
      className={`ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-semibold px-2.5 py-1 ring-1 ring-emerald-200 whitespace-nowrap ${veryFresh ? "animate-pulse" : ""}`}
      data-testid="edit-saved-indicator"
    >
      <Check className="h-3 w-3" />
      Tallennettu · {label}
    </span>
  );
}

function MaterialsEditor({ materials, savedMaterials, onChange, unitId }) {
  const list = Array.isArray(materials) ? materials : [];
  const [name, setName] = useState("");
  const [qty, setQty] = useState("");
  const [unitLabel, setUnitLabel] = useState("kpl");

  const addItem = (payload) => {
    const n = (payload?.name ?? name).trim();
    if (!n) return;
    const q = payload?.qty ?? (Number(qty) || 1);
    const u = ((payload?.unit ?? unitLabel) || "kpl").toString().trim() || "kpl";
    const existing = list.findIndex((m) => (m.name || "").toLowerCase() === n.toLowerCase());
    if (existing >= 0) {
      const merged = [...list];
      const cur = Number(merged[existing].qty) || 0;
      merged[existing] = { ...merged[existing], qty: cur + Number(q), unit: u };
      onChange(merged);
    } else {
      onChange([...list, { name: n, qty: Number(q), unit: u }]);
    }
    setName(""); setQty(""); setUnitLabel("kpl");
  };

  const updateRow = (i, patch) => onChange(list.map((m, idx) => idx === i ? { ...m, ...patch } : m));
  const removeRow = (i) => onChange(list.filter((_, idx) => idx !== i));
  const picks = (savedMaterials || []).map((m) => ({ name: (m.name || "").toString(), unit: m.unit || "kpl" })).filter((p) => p.name);

  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Käytetyt tarvikkeet</div>
      {picks.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {picks.map((p, i) => (
            <button
              key={`${p.name}-${i}`}
              type="button"
              onClick={() => addItem({ name: p.name, qty: 1, unit: p.unit })}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-white border border-blue-200 text-blue-800 hover:bg-blue-50"
              data-testid={`edit-unit-mat-pick-${unitId}-${i}`}
            >
              + {p.name} <span className="text-blue-400 font-normal">({p.unit || "kpl"})</span>
            </button>
          ))}
        </div>
      )}
      {list.length > 0 && (
        <div className="space-y-1.5 mb-2">
          {list.map((m, i) => {
            const curQty = Number(m.qty) || 0;
            const step = 1;
            return (
              <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200" data-testid={`edit-unit-mat-row-${unitId}-${i}`}>
                <span className="flex-1 text-sm text-slate-800 truncate">{m.name}</span>
                <div className="inline-flex items-center rounded-lg border border-blue-200 bg-white overflow-hidden">
                  <button
                    type="button"
                    onClick={() => {
                      const next = Math.max(0, curQty - step);
                      if (next === 0) removeRow(i);
                      else updateRow(i, { qty: next });
                    }}
                    className="h-8 w-8 flex items-center justify-center text-blue-700 hover:bg-blue-50 disabled:opacity-40"
                    disabled={curQty <= 0}
                    aria-label="Vähennä"
                    data-testid={`edit-unit-mat-dec-${unitId}-${i}`}
                  >
                    –
                  </button>
                  <input
                    type="number" min="0" step="0.1"
                    value={m.qty}
                    onChange={(e) => updateRow(i, { qty: e.target.value === "" ? "" : Number(e.target.value) })}
                    className="w-12 h-8 px-1 text-sm text-center bg-white border-l border-r border-blue-200 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => updateRow(i, { qty: curQty + step })}
                    className="h-8 w-8 flex items-center justify-center text-blue-700 hover:bg-blue-50"
                    aria-label="Lisää"
                    data-testid={`edit-unit-mat-inc-${unitId}-${i}`}
                  >
                    +
                  </button>
                </div>
                <span className="text-xs text-blue-700 font-medium w-10 text-left">{m.unit || "kpl"}</span>
                <button type="button" onClick={() => removeRow(i)} className="p-1 rounded text-slate-400 hover:text-red-600" aria-label="Poista">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
      <div className="space-y-1.5">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addItem(); } }}
          placeholder="Tarvike"
          className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
          data-testid={`edit-unit-mat-name-${unitId}`}
        />
        <div className="flex gap-1.5">
          <input
            type="number" min="0" step="0.1"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            placeholder="Määrä"
            inputMode="decimal"
            className="w-20 px-2 py-2 rounded-lg border border-slate-200 bg-white text-sm text-right"
          />
          <input
            value={unitLabel}
            onChange={(e) => setUnitLabel(e.target.value)}
            placeholder="kpl"
            className="w-16 px-2 py-2 rounded-lg border border-slate-200 bg-white text-sm"
          />
          <button
            type="button"
            onClick={() => addItem()}
            disabled={!name.trim()}
            className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-emerald-500 text-white text-sm font-semibold disabled:opacity-50 hover:bg-emerald-600"
            data-testid={`edit-unit-mat-add-${unitId}`}
          >
            <Plus className="h-4 w-4" /> Lisää
          </button>
        </div>
      </div>
    </div>
  );
}

function ManageDialog({ units, onClose, onAdd, onDelete, onRename }) {
  const [bulkText, setBulkText] = useState("");
  const [singleName, setSingleName] = useState("");

  const addOne = () => {
    if (!singleName.trim()) return;
    onAdd(singleName);
    setSingleName("");
  };
  const addBulk = () => {
    const lines = bulkText.split("\n").map((s) => s.trim()).filter(Boolean);
    lines.forEach((l) => onAdd(l));
    setBulkText("");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" data-testid="edit-manage-dialog">
      <div className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[85vh] flex flex-col">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-slate-900">Hallinnoi yksiköitä</h2>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-700 rounded-lg" data-testid="edit-manage-dialog-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-5 py-4 overflow-y-auto space-y-5">
          <section>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Lisää yksittäinen</div>
            <div className="flex gap-2">
              <input
                value={singleName}
                onChange={(e) => setSingleName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOne(); } }}
                placeholder="esim. Asunto 6"
                className="flex-1 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
                data-testid="edit-manage-single-input"
              />
              <button onClick={addOne} disabled={!singleName.trim()} className="px-3 py-2 rounded-lg bg-emerald-500 text-white text-sm font-semibold disabled:opacity-50" data-testid="edit-manage-single-add">
                Lisää
              </button>
            </div>
          </section>

          <section>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Lisää monta kerralla</div>
            <textarea
              rows={4}
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              placeholder="Asunto 6&#10;Asunto 7&#10;Asunto 8"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
              data-testid="edit-manage-bulk-input"
            />
            <button onClick={addBulk} disabled={!bulkText.trim()} className="mt-2 inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-emerald-500 text-white text-sm font-semibold disabled:opacity-50" data-testid="edit-manage-bulk-add">
              <Plus className="h-4 w-4" /> Lisää kaikki
            </button>
          </section>

          <section>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Nykyiset yksiköt ({units.length})</div>
            <div className="space-y-1.5">
              {units.map((u) => (
                <EditableUnitRow
                  key={u.id}
                  unit={u}
                  onRename={onRename}
                  onDelete={onDelete}
                />
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}


/**
 * EditableUnitRow — yksi rivi ManageDialogin nykyiset-listalla.
 * - Näyttää yksikön nimen ja "muokkaa"-kynän + roskakorin.
 * - Klikkaus nimeä tai kynää → inline-editointi (input + Tallenna/Peruuta).
 * - Enter tallentaa, Escape peruuttaa. Sama nimi ei laukaise tallennusta.
 */
function EditableUnitRow({ unit, onRename, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(unit.label);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (!editing) setValue(unit.label); }, [unit.label, editing]);
  const commit = async () => {
    const trimmed = (value || "").trim();
    if (!trimmed || trimmed === unit.label) { setEditing(false); setValue(unit.label); return; }
    setSaving(true);
    try { await onRename(unit.id, trimmed); }
    finally { setSaving(false); setEditing(false); }
  };
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200" data-testid={`edit-manage-row-${unit.id}`}>
      {editing ? (
        <>
          <input
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); commit(); }
              if (e.key === "Escape") { e.preventDefault(); setValue(unit.label); setEditing(false); }
            }}
            className="flex-1 px-2 py-1 rounded-md border border-emerald-300 bg-white text-sm outline-none focus:border-emerald-500"
            data-testid={`edit-manage-rename-input-${unit.id}`}
          />
          <button
            onClick={commit}
            disabled={saving}
            className="px-2 py-1 rounded-md bg-emerald-500 text-white text-xs font-semibold disabled:opacity-50"
            data-testid={`edit-manage-rename-save-${unit.id}`}
          >
            {saving ? "…" : "Tallenna"}
          </button>
          <button
            onClick={() => { setValue(unit.label); setEditing(false); }}
            className="px-2 py-1 rounded-md text-slate-500 hover:bg-slate-100 text-xs font-semibold"
            data-testid={`edit-manage-rename-cancel-${unit.id}`}
          >
            Peruuta
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="flex-1 text-left text-sm text-slate-800 hover:text-emerald-700 font-medium truncate"
            data-testid={`edit-manage-rename-open-${unit.id}`}
            title="Muokkaa nimeä"
          >
            {unit.label}
          </button>
          <button
            onClick={() => setEditing(true)}
            className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-700 hover:bg-emerald-50"
            aria-label="Muokkaa nimeä"
            data-testid={`edit-manage-rename-${unit.id}`}
          >
            <PencilLine className="h-4 w-4" />
          </button>
          <button
            onClick={() => onDelete(unit.id)}
            className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50"
            aria-label="Poista yksikkö"
            data-testid={`edit-manage-delete-${unit.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </>
      )}
    </div>
  );
}


function PhotoEditDialog({ photo, editToken, onClose, onSaved }) {
  const [caption, setCaption] = useState(photo?.caption || "");
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const saveCaption = async () => {
    if ((caption || "") === (photo?.caption || "")) {
      onClose();
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.patch(
        `${API}/reports/edit/${editToken}/photos/${photo.photo_id}`,
        { caption }
      );
      onSaved(data?.report);
      toast.success("Kuvateksti tallennettu");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tallennus epäonnistui");
    } finally {
      setBusy(false);
    }
  };

  const deletePhoto = async () => {
    if (!window.confirm("Poistetaanko kuva?")) return;
    setDeleting(true);
    try {
      const { data } = await axios.delete(
        `${API}/reports/edit/${editToken}/photos/${photo.photo_id}`
      );
      onSaved(data?.report);
      toast.success("Kuva poistettu");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Poisto epäonnistui");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="edit-photo-dialog">
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative">
          <img
            src={`${API}/photos/${photo.photo_id}`}
            alt={caption}
            className="w-full max-h-[55vh] object-contain bg-slate-900 rounded-t-2xl"
          />
          <button
            type="button"
            onClick={onClose}
            className="absolute top-2 right-2 h-9 w-9 rounded-full bg-white/95 hover:bg-white shadow flex items-center justify-center"
            aria-label="Sulje"
            data-testid="edit-photo-close"
          >
            <X className="h-4 w-4 text-slate-700" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <label className="block">
            <span className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Kuvateksti</span>
            <textarea
              rows={3}
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="esim. Suodatin vaihdettu, vanha kuluneena"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid="edit-photo-caption"
              autoFocus
            />
          </label>
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={deletePhoto}
              disabled={deleting || busy}
              className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-lg bg-white border border-red-200 text-red-700 hover:bg-red-50 text-sm font-semibold disabled:opacity-50"
              data-testid="edit-photo-delete"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Poista
            </button>
            <button
              type="button"
              onClick={saveCaption}
              disabled={busy || deleting}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 text-sm font-semibold disabled:opacity-50"
              data-testid="edit-photo-save"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Tallenna
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}




/* ------------------------------------------------------------------ */
/* Mobile UX v2 — Phase 5: drill-down views (replaces UnitEditor)      */
/* ------------------------------------------------------------------ */

function Level1Header({ report, lastSavedAt, onPreview, onPatchInfo, onMove }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(report.title || "");
  const timerRef = useRef(null);
  useEffect(() => { if (!editing) setVal(report.title || ""); }, [report.title, editing]);
  const commit = (next) => {
    setVal(next);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const t = (next || "").trim();
      if (t && t !== (report.title || "")) onPatchInfo?.({ title: t });
    }, 500);
  };
  return (
    <header className="bg-white border-b border-slate-100 sticky top-0 z-10">
      <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          {editing ? (
            <input
              autoFocus
              value={val}
              onChange={(e) => commit(e.target.value)}
              onBlur={() => setEditing(false)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") { e.currentTarget.blur(); } }}
              className="w-full bg-slate-50 border border-emerald-300 focus:border-emerald-500 outline-none rounded-lg px-2 py-1 font-display text-base font-semibold text-slate-900"
              data-testid="edit-report-title-input"
              placeholder="Työn nimi"
            />
          ) : (
            <button
              onClick={() => setEditing(true)}
              className="w-full text-left font-display text-base font-semibold text-slate-900 truncate hover:text-emerald-700 transition-colors"
              data-testid="edit-report-title"
              title="Muokkaa työn nimeä"
            >
              {report.title || "Nimeä työ…"}
            </button>
          )}
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-4 pb-3 -mt-1 flex items-center gap-2 overflow-x-auto">
        <button onClick={onPreview} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-xs font-semibold text-slate-700 whitespace-nowrap" data-testid="edit-preview-btn">
          <Eye className="h-3.5 w-3.5" /> Esikatselu
        </button>
        {onMove && (
          <button
            onClick={onMove}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-xs font-semibold text-emerald-700 whitespace-nowrap ring-1 ring-emerald-100"
            data-testid="edit-move-property-btn"
          >
            <Building2 className="h-3.5 w-3.5" /> Siirrä kohteeseen
          </button>
        )}
        <SavedIndicator at={lastSavedAt} />
      </div>
    </header>
  );
}

function DrillHeader({ eyebrow, title, onBack, backLabel, right, lastSavedAt }) {
  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
      <div className="max-w-3xl mx-auto px-3 py-2.5 flex items-center gap-2">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 px-3 py-2 -ml-1 rounded-full bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-800 text-sm font-semibold whitespace-nowrap transition-colors"
          data-testid="drill-back"
          aria-label={`Takaisin${backLabel ? ` — ${backLabel}` : ""}`}
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="max-w-[120px] truncate">{backLabel || "Takaisin"}</span>
        </button>
        <div className="flex-1 min-w-0">
          {eyebrow && (
            <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold truncate">{eyebrow}</div>
          )}
          <div className="font-display text-[15px] font-semibold text-slate-900 truncate" data-testid="drill-title">{title}</div>
        </div>
        {right}
      </div>
      <div className="max-w-3xl mx-auto px-3 pb-1.5 flex items-center justify-end">
        <SavedIndicator at={lastSavedAt} />
      </div>
    </header>
  );
}

function UnitStatusPill({ status }) {
  if (status === "done") return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-700">Valmis</span>;
  if (status === "inaccessible") return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-100 text-red-700">Estynyt</span>;
  return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-600">Avoin</span>;
}

function derivedStatus(unit) {
  if (unit.status === "done") return "done";
  if (unit.status === "inaccessible") return "inaccessible";
  // Heuristic for "in progress": any photo, any checklist toggle, any text
  const hasPhoto = (unit.photos || []).length > 0;
  const hasChecklist = (unit.checklist || []).some((c) => c.checked);
  const hasNotes = (unit.notes || "").trim() || (unit.additional_work_text || "").trim() || (unit.description || "").trim();
  const hasMaterials = (unit.materials_used || []).length > 0;
  if (hasPhoto || hasChecklist || hasNotes || hasMaterials) return "in_progress";
  return "pending";
}

/* ============ Level 1: Unit list ============ */

function Level1UnitList({ report, units, onOpenUnit, onOpenCamera, onManageUnits, onMarkAllDone, editToken, onSummarySaved, onPatchInfo }) {
  const stats = {
    total: units.length,
    done: units.filter((u) => u.status === "done").length,
    blocked: units.filter((u) => u.status === "inaccessible").length,
  };
  const remaining = stats.total - stats.done - stats.blocked;

  return (
    <div data-testid="edit-level-1">
      {/* Perustiedot — kenttätyöntekijä voi täydentää työn tiedot lennossa */}
      <ReportInfoPanel report={report} onPatchInfo={onPatchInfo} />
      {/* Projekti- ja Eteneminen-laatikot poistettu käyttäjän pyynnöstä:
          - Per-yksikön "Merkitse valmiiksi" -nappi on jo poistettu, joten
            yksilöllinen edistymäindikaattori (Valmis/Kesken/Aloittamatta) on
            tarpeeton — kentällä halutaan vain "tee homma valmiiksi" -flow.
          - Projektitiedot näkyvät toimiston dashboardissa, ei asentajan UI:ssa. */}

      {/* Unit rows */}
      <div className="rounded-2xl bg-white border border-slate-100 overflow-hidden">
        {units.map((u, i) => (
          <UnitRow
            key={u.id}
            unit={u}
            onOpen={() => onOpenUnit(u)}
            onOpenCamera={onOpenCamera}
            isLast={i === units.length - 1}
          />
        ))}
        {units.length === 0 && (
          <div className="p-6 text-center text-sm text-slate-500">Ei yksiköitä. Lisää alta.</div>
        )}
      </div>

      {/* Editable summary — shows up after MarkAllDone generates one OR if office
          has already drafted it. Field worker can refine before office finalizes. */}
      {report.ai_summary && (
        <SummaryCard report={report} editToken={editToken} onSaved={onSummarySaved} />
      )}

      <div className="mt-6 flex flex-col items-stretch gap-3">
        {stats.total > 0 && remaining > 0 && (
          <button
            onClick={onMarkAllDone}
            className="inline-flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-sm shadow-sm"
            data-testid="edit-mark-all-done-btn"
          >
            <CheckCheck className="h-5 w-5" /> Merkitse koko kohde valmiiksi
            {remaining > 0 && (
              <span className="text-xs font-normal opacity-90">· {remaining} yksikkö{remaining === 1 ? "" : "ä"} avoinna</span>
            )}
          </button>
        )}
        <button
          onClick={onManageUnits}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-sm font-semibold"
          data-testid="edit-manage-units-btn"
        >
          <Settings className="h-4 w-4" /> Hallinnoi yksiköitä
        </button>

        {/* Yhteenvedot: havainnot ja tarvikkeet kaikista yksiköistä */}
        <UnitFindingsCollapsible units={units} />
        <UnitMaterialsCollapsible units={units} />
      </div>
    </div>
  );
}

/* ============================================================================
 * UnitFindingsCollapsible — koota kaikkien yksiköiden havainnot yhteen listaan,
 * jonka voi avata ja kopioida leikepöydälle.
 * ========================================================================== */
function ReportInfoPanel({ report, onPatchInfo }) {
  const filled = [report.customer_name, report.site_address, report.report_date, report.property_type].filter((v) => (v || "").toString().trim()).length;
  const storageKey = `veraso_info_open_${report.edit_token || report.id || ""}`;
  // Avaa oletuksena kun jotain kenttää puuttuu, mutta jos käyttäjä on itse
  // sulkenut paneelin, kunnioita valintaa myös seuraavilla käynneillä.
  const [open, setOpen] = useState(() => {
    try {
      const saved = window.localStorage.getItem(storageKey);
      if (saved === "0") return false;
      if (saved === "1") return true;
    } catch { /* ignore */ }
    return filled < 3;
  });
  const toggle = () => {
    setOpen((v) => {
      const next = !v;
      try { window.localStorage.setItem(storageKey, next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };
  return (
    <div className={`mb-4 rounded-2xl border-2 overflow-hidden transition-colors ${filled < 3 ? "bg-amber-50/40 border-amber-200" : "bg-white border-slate-200"}`} data-testid="edit-info-panel">
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center gap-3 px-4 py-3.5 text-left"
        data-testid="edit-info-toggle"
      >
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center ring-1 flex-shrink-0 ${filled < 3 ? "bg-amber-100 text-amber-700 ring-amber-200" : "bg-emerald-50 text-emerald-600 ring-emerald-100"}`}>
          <FileText className="h-4.5 w-4.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900 leading-tight">Työn perustiedot</div>
          <div className="text-xs text-slate-500 mt-0.5 truncate">
            {filled === 0 ? "Lisää asiakas, osoite ja päivämäärä" : `${filled}/4 täytetty · ${report.customer_name || "Ei asiakasta"}`}
          </div>
        </div>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-slate-100 p-4 space-y-3 bg-white/70">
          <InfoInput
            label="Asiakas"
            value={report.customer_name || ""}
            placeholder="esim. As Oy Rantatie 12"
            onSave={(v) => onPatchInfo?.({ customer_name: v })}
            testId="edit-info-customer"
          />
          <InfoInput
            label="Osoite"
            value={report.site_address || ""}
            placeholder="Työn osoite"
            onSave={(v) => onPatchInfo?.({ site_address: v })}
            testId="edit-info-address"
          />
          <InfoInput
            label="Kohteen tyyppi"
            value={report.property_type || ""}
            placeholder="esim. Kerrostalo, Rivitalo"
            onSave={(v) => onPatchInfo?.({ property_type: v })}
            testId="edit-info-property-type"
          />
          <InfoDate
            label="Päivämäärä"
            value={report.report_date || ""}
            onSave={(v) => onPatchInfo?.({ report_date: v })}
            testId="edit-info-date"
          />
        </div>
      )}
    </div>
  );
}

function InfoInput({ label, value, placeholder, onSave, testId }) {
  const [v, setV] = useState(value || "");
  // Sentinel-pohjainen lukko: timerRef ei ole null niin kauan kuin käyttäjä
  // kirjoittaa tai tallennus on käynnissä. useEffect ohittaa prop-synkronoinnin
  // koko ajan — näin PATCHin palauttama vastaus ei koskaan ylikirjoita
  // käyttäjän tuoreimpia näppäilyjä (iter78 bugikorjaus).
  const timerRef = useRef(null);
  const localRef = useRef(value || "");
  useEffect(() => {
    if (timerRef.current !== null) return;
    const next = value || "";
    if (localRef.current === next) return;
    localRef.current = next;
    setV(next);
  }, [value]);
  const change = (next) => {
    setV(next);
    localRef.current = next;
    if (timerRef.current) {
      clearTimeout(timerRef.current.id);
    }
    const h = { id: null };
    timerRef.current = h;
    h.id = setTimeout(async () => {
      const toSave = localRef.current;
      try {
        await onSave(toSave);
      } finally {
        // Vapauta lukko vain jos käyttäjä ei ole aloittanut uutta syötettä
        // await:n aikana — muuten jätä seuraavalle timerille.
        if (timerRef.current === h) {
          timerRef.current = null;
        }
      }
    }, 500);
  };
  const commit = () => {
    if (!timerRef.current) return;
    clearTimeout(timerRef.current.id);
    const toSave = localRef.current;
    timerRef.current = null;
    onSave(toSave);
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
      <input
        value={v}
        onChange={(e) => change(e.target.value)}
        onBlur={commit}
        placeholder={placeholder || "—"}
        className="w-full bg-transparent outline-none text-sm font-medium text-slate-900"
        data-testid={testId}
      />
    </div>
  );
}

function InfoDate({ label, value, onSave, testId }) {
  const toDateInput = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return "";
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    } catch { return ""; }
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
      <input
        type="date"
        value={toDateInput(value)}
        onChange={(e) => onSave(e.target.value)}
        className="w-full bg-transparent outline-none text-sm font-medium text-slate-900"
        data-testid={testId}
      />
    </div>
  );
}


/* ============================================================================
 * UnitFindingsCollapsible — koota kaikkien yksiköiden havainnot yhteen listaan,
 * jonka voi avata ja kopioida leikepöydälle.
 * ========================================================================== */
function UnitFindingsCollapsible({ units }) {
  const [open, setOpen] = useState(false);
  const findings = [];
  for (const u of units) {
    const text = (u.additional_work_text || "").trim();
    // Checklist-item käyttää backendin mukaista `label`-kenttää.
    // Kaadutaan takaisin `text`/`name` legacy-arvoihin jos ne sattuvat olemaan olemassa.
    const checkedFindings = (u.checklist || [])
      .filter((c) => c && c.is_finding && c.checked)
      .map((c) => (c.label || c.text || c.name || "").toString().trim())
      .filter(Boolean);
    if (text || checkedFindings.length > 0) {
      findings.push({
        unitId: u.id,
        unitName: u.label || u.name || u.description || "Yksikkö",
        text,
        checks: checkedFindings,
      });
    }
  }
  const count = findings.length;

  const copyAll = async () => {
    const lines = [];
    for (const f of findings) {
      lines.push(f.unitName + ":");
      for (const c of f.checks) lines.push("  • " + c);
      if (f.text) lines.push("  " + f.text);
      lines.push("");
    }
    const payload = lines.join("\n").trimEnd();
    try {
      await navigator.clipboard.writeText(payload);
      toast.success("Havainnot kopioitu leikepöydälle");
    } catch {
      toast.error("Kopiointi epäonnistui");
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="edit-findings-collapsible">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50"
        data-testid="edit-findings-toggle"
      >
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 ${count > 0 ? "bg-amber-50 text-amber-600 ring-1 ring-amber-100" : "bg-slate-50 text-slate-400"}`}>
          <AlertTriangle className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900">Näytä havainnot</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {count === 0 ? "Ei havaintoja" : `${count} ${count === 1 ? "yksikkö" : "yksikköä"} · yhteenveto kaikista havainnoista`}
          </div>
        </div>
        <ChevronDown className={`h-4 w-4 text-slate-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-slate-100 bg-slate-50/60 p-3 space-y-2" data-testid="edit-findings-list">
          {count === 0 && (
            <div className="text-xs text-slate-500 text-center py-4">Kentältä ei ole vielä havaintoja.</div>
          )}
          {findings.map((f) => (
            <div key={f.unitId} className="rounded-lg bg-white border border-slate-200 p-3">
              <div className="text-[11px] uppercase tracking-wider text-amber-700 font-semibold mb-1">{f.unitName}</div>
              {f.checks.length > 0 && (
                <ul className="mb-1 space-y-0.5">
                  {f.checks.map((c, i) => (
                    <li key={i} className="text-sm text-slate-800 pl-3 relative before:content-['•'] before:absolute before:left-0 before:text-amber-500">{c}</li>
                  ))}
                </ul>
              )}
              {f.text && (
                <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{f.text}</div>
              )}
            </div>
          ))}
          {count > 0 && (
            <button
              type="button"
              onClick={copyAll}
              className="w-full mt-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-white border border-slate-200 hover:border-slate-300 text-slate-700 text-xs font-semibold"
              data-testid="edit-findings-copy"
            >
              <Copy className="h-3.5 w-3.5" /> Kopioi kaikki havainnot
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ============================================================================
 * UnitMaterialsCollapsible — koota kaikkien yksiköiden tarvikkeet ja niiden
 * määrät yhteen tiivistelmään, jonka voi kopioida.
 * ========================================================================== */
function UnitMaterialsCollapsible({ units }) {
  const [open, setOpen] = useState(false);
  // Aggregoi tarvikkeet nimen+yksikön mukaan
  const totals = new Map();
  const perUnit = [];
  for (const u of units) {
    const items = (u.materials_used || []).filter((m) => (m.name || "").trim() && Number(m.qty) > 0);
    if (items.length === 0) continue;
    perUnit.push({ unitId: u.id, unitName: u.label || u.name || u.description || "Yksikkö", items });
    for (const m of items) {
      const key = `${(m.name || "").trim()}||${m.unit || "kpl"}`;
      const prev = totals.get(key) || { name: m.name.trim(), unit: m.unit || "kpl", qty: 0 };
      prev.qty += Number(m.qty) || 0;
      totals.set(key, prev);
    }
  }
  const totalsArr = Array.from(totals.values()).sort((a, b) => a.name.localeCompare(b.name, "fi"));
  const count = perUnit.length;

  const copyAll = async () => {
    const lines = ["Yhteensä:"];
    for (const t of totalsArr) lines.push(`  ${t.name} — ${t.qty} ${t.unit}`);
    lines.push("");
    for (const p of perUnit) {
      lines.push(p.unitName + ":");
      for (const m of p.items) lines.push(`  ${m.name} — ${m.qty} ${m.unit || "kpl"}`);
      lines.push("");
    }
    try {
      await navigator.clipboard.writeText(lines.join("\n").trimEnd());
      toast.success("Tarvikkeet kopioitu leikepöydälle");
    } catch {
      toast.error("Kopiointi epäonnistui");
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="edit-materials-collapsible">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50"
        data-testid="edit-materials-toggle"
      >
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 ${count > 0 ? "bg-violet-50 text-violet-600 ring-1 ring-violet-100" : "bg-slate-50 text-slate-400"}`}>
          <Package className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900">Näytä tarvikkeet</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {count === 0 ? "Ei tarvikkeita" : `${count} ${count === 1 ? "yksiköllä" : "yksiköllä"} tarvikkeita · ${totalsArr.length} eri riviä`}
          </div>
        </div>
        <ChevronDown className={`h-4 w-4 text-slate-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-slate-100 bg-slate-50/60 p-3 space-y-3" data-testid="edit-materials-list">
          {count === 0 && (
            <div className="text-xs text-slate-500 text-center py-4">Kentältä ei ole vielä tarvikkeita.</div>
          )}
          {count > 0 && (
            <>
              <div className="rounded-lg bg-white border border-violet-200 p-3">
                <div className="text-[11px] uppercase tracking-wider text-violet-700 font-semibold mb-1.5">Yhteensä</div>
                <ul className="space-y-1">
                  {totalsArr.map((t, i) => (
                    <li key={i} className="flex items-baseline gap-2 text-sm text-slate-800">
                      <span className="flex-1 truncate">{t.name}</span>
                      <span className="tabular-nums font-semibold text-slate-900">{t.qty}</span>
                      <span className="text-xs text-slate-500 w-8 text-left">{t.unit}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="space-y-2">
                {perUnit.map((p) => (
                  <div key={p.unitId} className="rounded-lg bg-white border border-slate-200 p-3">
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{p.unitName}</div>
                    <ul className="space-y-0.5">
                      {p.items.map((m, i) => (
                        <li key={i} className="flex items-baseline gap-2 text-sm text-slate-800">
                          <span className="flex-1 truncate">{m.name}</span>
                          <span className="tabular-nums text-slate-900">{m.qty}</span>
                          <span className="text-xs text-slate-500 w-8 text-left">{m.unit || "kpl"}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={copyAll}
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-white border border-slate-200 hover:border-slate-300 text-slate-700 text-xs font-semibold"
                data-testid="edit-materials-copy"
              >
                <Copy className="h-3.5 w-3.5" /> Kopioi tarvikelista
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function UnitRow({ unit, onOpen, onOpenCamera, isLast }) {
  const ds = derivedStatus(unit);
  const dot =
    ds === "done" ? <Check className="h-4 w-4 text-emerald-600" strokeWidth={3} /> :
    ds === "inaccessible" ? <X className="h-4 w-4 text-red-600" strokeWidth={3} /> :
    ds === "in_progress" ? <span className="h-3 w-3 rounded-full bg-amber-500" /> :
    <span className="h-3 w-3 rounded-full border-2 border-slate-300" />;
  const dotBg =
    ds === "done" ? "bg-emerald-100" :
    ds === "inaccessible" ? "bg-red-100" :
    "bg-white";
  const photoCount = (unit.photos || []).length;

  // Tap (single = drill in, double = camera) + Snapchat-style swipe-left = camera
  const tapTimerRef = useRef(null);
  const lastTapRef = useRef(0);
  const swipedRef = useRef(false);
  const touchStartRef = useRef({ x: 0, y: 0 });
  useEffect(() => () => { if (tapTimerRef.current) clearTimeout(tapTimerRef.current); }, []);

  const handleClick = () => {
    if (swipedRef.current) { swipedRef.current = false; return; }
    if (!onOpenCamera) { onOpen(); return; }
    const now = Date.now();
    if (now - lastTapRef.current < 280) {
      if (tapTimerRef.current) { clearTimeout(tapTimerRef.current); tapTimerRef.current = null; }
      lastTapRef.current = 0;
      onOpenCamera(unit);
    } else {
      lastTapRef.current = now;
      tapTimerRef.current = setTimeout(() => {
        lastTapRef.current = 0;
        tapTimerRef.current = null;
        onOpen();
      }, 280);
    }
  };
  const handleTouchStart = (e) => {
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY };
  };
  const handleTouchEnd = (e) => {
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStartRef.current.x;
    const dy = t.clientY - touchStartRef.current.y;
    if (onOpenCamera && dx < -50 && Math.abs(dy) < 40) {
      swipedRef.current = true;
      onOpenCamera(unit);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      className={`w-full flex items-center gap-3 px-4 py-4 text-left hover:bg-slate-50/80 transition-colors touch-pan-y select-none ${isLast ? "" : "border-b border-slate-100"}`}
      data-testid={`edit-unit-row-${unit.id}`}
    >
      <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${dotBg}`}>
        {dot}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-semibold text-slate-900 text-[17px] leading-tight truncate">{unit.label}</div>
        {photoCount > 0 && (
          <div className="text-xs text-slate-400 mt-1 inline-flex items-center gap-1">
            <Camera className="h-3 w-3" /> {photoCount} {photoCount === 1 ? "kuva" : "kuvaa"}
          </div>
        )}
      </div>
      <UnitStatusPill status={unit.status} />
      <ChevronRight className="h-4 w-4 text-slate-300 flex-shrink-0 -ml-1" />
    </button>
  );
}

/* ============ Level 2: Unit hub ============ */

function Level2UnitHub({ unit, report, onOpenSection, onOpenSingle, onOpenGallery }) {
  const photoCount = (unit.photos || []).length;
  const findingPhotos = (unit.photos || []).filter((p) => p.is_finding).length;
  const workChecklist = (unit.checklist || []).filter((c) => !c.is_finding);
  const findingChecklist = (unit.checklist || []).filter((c) => c.is_finding);
  const workDone = workChecklist.filter((c) => c.checked).length;
  const findingDone = findingChecklist.filter((c) => c.checked).length;
  const additionalText = (unit.additional_work_text || "").trim();
  const description = (unit.description || "").trim();
  const materials = unit.materials_used || [];
  const measurementEntries = unit.measurement_entries || [];
  const devices = unit.devices || [];
  const measurementCount = measurementEntries.length;
  const deviceCount = devices.length;
  const customFieldDefs = report?.custom_fields || [];
  const customValues = unit.custom_values || {};
  const customFilledCount = customFieldDefs.filter(
    (f) => ((customValues[f.id] || "").toString().trim() !== ""),
  ).length;

  // Yhteenveto "Työ"-napille: yhdistää työvaiheet, tehdyt toimenpiteet ja havainnot.
  const workTotalItems = workChecklist.length + findingChecklist.length;
  const workDoneItems = workDone + findingDone;
  const workHasFindings = additionalText || findingChecklist.some((c) => c.checked);
  const workSub = (() => {
    if (workTotalItems > 0) return `${workDoneItems}/${workTotalItems} merkitty${workHasFindings ? " · havaintoja" : ""}`;
    if (description || additionalText) return workHasFindings ? "Tehty · havaintoja" : "Merkintöjä kirjattu";
    return "Ei merkintöjä";
  })();

  const hubItems = [
    {
      key: "photos",
      icon: ImageIcon,
      title: "Kuvat",
      sub: photoCount === 0 ? "Ei kuvia" : `${photoCount} ${photoCount === 1 ? "kuva" : "kuvaa"}${findingPhotos > 0 ? ` · ${findingPhotos} havainto` : ""}`,
      badge: photoCount > 0 ? photoCount : null,
    },
    {
      key: "work",
      icon: ListChecks,
      title: "Tehdyt toimenpiteet / Lisätiedot",
      sub: workSub,
      badge: workHasFindings ? "!" : (workTotalItems > 0 ? `${workDoneItems}/${workTotalItems}` : null),
      accent: workHasFindings,
    },
    {
      key: "materials",
      icon: Package,
      title: "Tarvikkeet",
      sub: materials.length === 0 ? "Ei tarvikkeita" : `${materials.length} riviä`,
      badge: materials.length > 0 ? materials.length : null,
    },
    // Mittaustulokset — mobiilinatiivi, aina saatavilla
    {
      key: "measurements",
      icon: Ruler,
      title: "Mittaustulokset",
      sub: measurementCount === 0
        ? "Ei mittauksia"
        : `${measurementCount} ${measurementCount === 1 ? "mittaus" : "mittausta"}`,
      badge: measurementCount > 0 ? measurementCount : null,
    },
    // Laitetiedot — mobiilinatiivi, aina saatavilla
    {
      key: "devices",
      icon: Cpu,
      title: "Laitetiedot",
      sub: deviceCount === 0
        ? "Ei laitteita"
        : `${deviceCount} ${deviceCount === 1 ? "laite" : "laitetta"}`,
      badge: deviceCount > 0 ? deviceCount : null,
    },
    // Muut kerättävät tiedot — vain jos raportilla määritelty custom_fields
    ...(customFieldDefs.length > 0 ? [{
      key: "custom",
      icon: FileText,
      title: "Muut tiedot",
      sub: customFilledCount === 0
        ? `${customFieldDefs.length} ${customFieldDefs.length === 1 ? "kenttä" : "kenttää"} täytettävänä`
        : `${customFilledCount}/${customFieldDefs.length} täytetty`,
      badge: customFilledCount > 0 ? `${customFilledCount}/${customFieldDefs.length}` : null,
    }] : []),
  ];

  return (
    <div data-testid="edit-level-2" className="space-y-4">
      {/* Per-yksikön "Merkitse valmiiksi" -nappi poistettu käyttäjän toiveesta —
          riittää että Level 1:ssä on "Merkitse koko kohde valmiiksi". Tehdyt-
          tarkistuslistat toimivat per-yksikkö-edistymisen indikaattoreina. */}

      {/* MIDDLE: 4 osio-riviä */}
      <div className="rounded-2xl bg-white border border-slate-100 overflow-hidden">
        {hubItems.map((item, i) => (
          <button
            key={item.key}
            type="button"
            onClick={() => onOpenSection(item.key)}
            className={`w-full flex items-center gap-3 px-4 py-4 text-left hover:bg-slate-50/80 transition-colors ${i < hubItems.length - 1 ? "border-b border-slate-100" : ""}`}
            data-testid={`edit-hub-${item.key}`}
          >
            <div className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 ${item.accent ? "bg-amber-100 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>
              <item.icon className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-semibold text-slate-900 text-[15px]">{item.title}</div>
              <div className="text-xs text-slate-500 mt-0.5 truncate">{item.sub}</div>
            </div>
            {item.badge !== null && (
              <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${item.accent ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                {item.badge}
              </span>
            )}
            <ChevronRight className="h-4 w-4 text-slate-300 flex-shrink-0" />
          </button>
        ))}
      </div>

      {/* BOTTOM: Aloita kuvien lisääminen — 2 pikavalintaa */}
      <div className="rounded-2xl bg-white border border-slate-100 p-4">
        <div className="mb-3">
          <div className="font-display font-semibold text-slate-900 text-[15px]">Aloita kuvien lisääminen</div>
          <div className="text-xs text-slate-500 mt-0.5">Dokumentoi työ ja lisää kuvat</div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onOpenGallery}
            className="flex flex-col items-center gap-2 p-3 rounded-xl bg-emerald-50/80 hover:bg-emerald-100 active:bg-emerald-200 border border-emerald-100 text-emerald-700 font-semibold text-xs sm:text-sm transition-colors"
            data-testid={`edit-hub-take-gallery-${unit.id}`}
          >
            <div className="h-10 w-10 rounded-full bg-emerald-500 text-white flex items-center justify-center">
              <ImageIcon className="h-5 w-5" />
            </div>
            Valitse galleriasta
          </button>
          {/* "Ota useita kuvia" -nappi poistettu käyttäjän pyynnöstä — 'Ota kuva'
              avaa Veraso-kameran joka tukee useita kuvia samalla istunnolla. */}
          <button
            type="button"
            onClick={onOpenSingle}
            className="flex flex-col items-center gap-2 p-3 rounded-xl bg-emerald-50/80 hover:bg-emerald-100 active:bg-emerald-200 border border-emerald-100 text-emerald-700 font-semibold text-xs sm:text-sm transition-colors"
            data-testid={`edit-hub-take-single-${unit.id}`}
          >
            <div className="h-10 w-10 rounded-full bg-emerald-500 text-white flex items-center justify-center">
              <Camera className="h-5 w-5" />
            </div>
            Ota kuva
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============ Level 3: Sections ============ */

function Level3Photos({ unit, editToken, onOpenPhoto, onOpenCamera, onPhotoSaved }) {
  const photos = unit.photos || [];
  return (
    <div data-testid="edit-level-3-photos">
      {photos.length === 0 ? (
        <div className="rounded-2xl bg-white border border-dashed border-slate-200 p-8 text-center">
          <Camera className="h-8 w-8 mx-auto text-slate-300 mb-3" />
          <div className="font-display font-semibold text-slate-700">Ei vielä kuvia</div>
          <div className="text-sm text-slate-500 mt-1">Lisää kuva klikkaamalla alla olevaa painiketta.</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {photos.map((p) => (
            <PhotoTileWithCaption
              key={p.photo_id}
              photo={p}
              unitId={unit.id}
              editToken={editToken}
              onOpenPhoto={onOpenPhoto}
              onPhotoSaved={onPhotoSaved}
            />
          ))}
        </div>
      )}
      <button
        type="button"
        onClick={onOpenCamera}
        className="mt-4 inline-flex items-center justify-center gap-2 w-full px-3 py-3.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold shadow-sm"
        data-testid={`edit-unit-open-camera-${unit.id}`}
      >
        <Camera className="h-5 w-5" /> Ota kuva
      </button>
    </div>
  );
}

/**
 * PhotoTileWithCaption — yksittäisen kuvan ruutu joka näyttää:
 *  - itse kuva (aspect-square)
 *  - INLINE caption-tekstilaatikko suoraan kuvan alla (debounced save onBlur)
 *  - klikkaus avaa per-kuva-näkymän (poisto + caption-edit)
 *
 * Käyttäjäpyynnön mukaan: useita kuvia kerralla otettaessa caption-laatikon ei
 * tarvitse vaatia erillistä klikkausta — se näkyy heti kuvan alla.
 * is_finding (havainto-flag per kuva) -toiminto on poistettu käytöstä.
 */
function PhotoTileWithCaption({ photo, unitId, editToken, onOpenPhoto, onPhotoSaved }) {
  const [caption, setCaption] = useState(photo.caption || "");
  const [saving, setSaving] = useState(false);

  // Pidä state synkassa kun photo-prop päivittyy parentista (esim. uudella fetch:llä).
  useEffect(() => {
    setCaption(photo.caption || "");
  }, [photo.caption]);

  const persist = async () => {
    const next = caption.trim();
    if (next === (photo.caption || "").trim()) return;
    setSaving(true);
    try {
      const { data } = await axios.patch(
        `${API}/reports/edit/${editToken}/photos/${photo.photo_id}`,
        { caption: next }
      );
      if (data?.report) onPhotoSaved?.(data.report);
    } catch {
      // PATCH epäonnistui — palautetaan UI:n caption serverin tilaan ja näytetään
      // toast jotta käyttäjä tietää että tallennus ei mennyt läpi.
      setCaption(photo.caption || "");
      toast.error("Kuvatekstin tallennus epäonnistui");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <button
        type="button"
        onClick={() => onOpenPhoto(photo)}
        className="relative block w-full aspect-square rounded-xl overflow-hidden border border-slate-100 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        data-testid={`edit-unit-photo-${unitId}-${photo.photo_id}`}
        aria-label="Avaa kuva tarkemmin"
      >
        <img
          src={`${API}/photos/${photo.photo_id}`}
          alt={photo.caption || ""}
          className="w-full h-full object-cover"
        />
      </button>
      <input
        type="text"
        value={caption}
        onChange={(e) => setCaption(e.target.value)}
        onBlur={persist}
        placeholder="Kuvateksti…"
        className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-xs text-slate-800 placeholder:text-slate-300 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none"
        data-testid={`edit-photo-caption-${photo.photo_id}`}
      />
      {saving && <div className="text-[10px] text-slate-400">Tallennetaan…</div>}
    </div>
  );
}


function Level3Work({ unit, onPatch }) {
  const workItems = (unit.checklist || [])
    .map((c, i) => ({ ...c, _origIdx: i }))
    .filter((c) => !c.is_finding);
  const findingItems = (unit.checklist || [])
    .map((c, i) => ({ ...c, _origIdx: i }))
    .filter((c) => c.is_finding);
  // Asentajan "Lisätiedot tehdystä työstä" — kirjoitetaan SAMAAN kenttään
  // kuin toimiston "Tehdyt toimenpiteet" (description). Aiemmin tallennettiin
  // legacy `notes`-kenttään, mikä ei näkynyt toimiston dashboardissa.
  const [notes, setNotes] = useState(unit.description || unit.notes || "");
  const [executedBy, setExecutedBy] = useState(unit.executed_by || "");
  const [followUpText, setFollowUpText] = useState(unit.additional_work_text || "");

  const toggleChecklist = (origIdx) => {
    const next = (unit.checklist || []).map((c, i) =>
      i === origIdx ? { ...c, checked: !c.checked } : c
    );
    onPatch({ checklist: next });
  };
  const saveNotes = () => {
    if (notes !== (unit.description || "")) onPatch({ description: notes });
  };
  const saveExecutedBy = () => {
    if (executedBy !== (unit.executed_by || "")) onPatch({ executed_by: executedBy });
  };
  const saveFollowUp = () => {
    if (followUpText !== (unit.additional_work_text || "")) {
      onPatch({
        additional_work_text: followUpText,
        additional_work_needed: !!followUpText.trim(),
      });
    }
  };

  return (
    <div className="space-y-6" data-testid="edit-level-3-work">
      {/* --- Työvaiheet + Tehdyt toimenpiteet --- */}
      {workItems.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Työvaiheet / Checklist</div>
          <div className="space-y-1.5">
            {workItems.map((c) => (
              <label key={c._origIdx} className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer ${
                c.checked ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-200"
              }`}>
                <input
                  type="checkbox"
                  checked={!!c.checked}
                  onChange={() => toggleChecklist(c._origIdx)}
                  className="h-5 w-5 accent-emerald-500"
                  data-testid={`edit-unit-checklist-${unit.id}-${c._origIdx}`}
                />
                <span className={`text-sm ${c.checked ? "text-slate-600 font-medium" : "text-slate-800"}`}>
                  {c.label}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div>
        <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Tehdyt toimenpiteet / Lisätiedot</label>
        <textarea
          rows={4}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={saveNotes}
          placeholder="Kerro mitä tehtiin tässä yksikössä…"
          className="w-full px-3 py-3 rounded-xl border border-slate-200 bg-white text-sm"
          data-testid={`edit-unit-notes-${unit.id}`}
        />
      </div>

      {/* --- Havainnot — visuaalinen erotus amber-sävyllä --- */}
      <div className="pt-4 border-t border-amber-100">
        <div className="text-xs uppercase tracking-wider text-amber-700 font-semibold mb-2 inline-flex items-center gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
          Havainnot
        </div>

        {findingItems.length > 0 && (
          <div className="mb-3">
            <div className="space-y-1.5">
              {findingItems.map((c) => (
                <label key={c._origIdx} className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer ${
                  c.checked ? "bg-amber-50 border-amber-300" : "bg-white border-amber-200"
                }`}>
                  <input
                    type="checkbox"
                    checked={!!c.checked}
                    onChange={() => toggleChecklist(c._origIdx)}
                    className="h-5 w-5 accent-amber-500"
                    data-testid={`edit-unit-finding-${unit.id}-${c._origIdx}`}
                  />
                  <span className={`text-sm flex items-center gap-2 ${c.checked ? "text-slate-700 font-medium" : "text-slate-800"}`}>
                    <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-amber-200 text-amber-900 text-[9px] font-bold">!</span>
                    {c.label}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        <textarea
          rows={5}
          value={followUpText}
          onChange={(e) => setFollowUpText(e.target.value)}
          onBlur={saveFollowUp}
          placeholder="esim. Vesivuoto pesukoneliitännässä, vaatii uusinnan ensi käynnillä. Voit kirjoittaa myös suositukset ja jatkotoimenpiteet tähän."
          className="w-full px-3 py-3 rounded-xl border-2 border-amber-200 bg-amber-50/30 focus:border-amber-400 focus:bg-white text-sm transition-colors"
          data-testid={`edit-unit-followup-${unit.id}`}
        />
        <div className="text-xs text-amber-700 mt-1.5 inline-flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          Tämä teksti näkyy raportin Havainnot-osiossa.
        </div>
      </div>

      {/* --- Työn suoritti — kaikkein alimmaiseksi (harvemmin muuttuva metatieto) --- */}
      <div className="pt-4 border-t border-slate-100">
        <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Työn suoritti</label>
        <input
          type="text"
          value={executedBy}
          onChange={(e) => setExecutedBy(e.target.value)}
          onBlur={saveExecutedBy}
          placeholder="esim. Matti Meikäläinen"
          className="w-full px-3 py-3 rounded-xl border border-slate-200 bg-white text-base"
          data-testid={`edit-unit-executed-by-${unit.id}`}
        />
      </div>
    </div>
  );
}

const MEAS_NAME_SUGGESTIONS = ["Ilmamäärä", "Paine", "Lämpötila", "Kosteus", "CO₂", "Sähkövirta", "Jännite", "Kierrosluku"];
const MEAS_UNIT_SUGGESTIONS = ["l/s", "m³/h", "Pa", "kPa", "°C", "%", "ppm", "A", "V", "dB", "rpm"];

function Level3Measurements({ unit, onPatch }) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const [unitStr, setUnitStr] = useState("");
  const entries = unit.measurement_entries || [];

  const commit = () => {
    const n = (name || "").trim();
    const v = (value || "").trim();
    const u = (unitStr || "").trim();
    if (!n && !v) { setAdding(false); return; }
    const newEntry = {
      id: Math.random().toString(36).slice(2, 12),
      name: n, value: v, unit: u, extra: {},
    };
    onPatch({ measurement_entries: [...entries, newEntry] });
    setName(""); setValue(""); setUnitStr(""); setAdding(false);
  };

  const remove = (id) => {
    onPatch({ measurement_entries: entries.filter((m) => m.id !== id) });
  };

  const updateEntry = (id, patch) => {
    onPatch({ measurement_entries: entries.map((m) => (m.id === id ? { ...m, ...patch } : m)) });
  };

  return (
    <div className="space-y-3" data-testid="edit-level-3-measurements">
      {entries.length === 0 && !adding && (
        <div className="rounded-2xl bg-white border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500">
          Ei mittauksia tallennettuna.
        </div>
      )}

      {entries.map((m, i) => (
        <div
          key={m.id}
          className="rounded-2xl bg-white border border-slate-200 p-3.5 space-y-2.5"
          data-testid={`edit-measurement-${m.id}`}
        >
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Mittaus {i + 1}</div>
            <button
              onClick={() => remove(m.id)}
              className="text-slate-300 hover:text-red-600 p-1"
              data-testid={`edit-measurement-remove-${m.id}`}
              aria-label="Poista mittaus"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <MeasField
            label="Mittaus"
            value={m.name || ""}
            onSave={(v) => updateEntry(m.id, { name: v })}
            placeholder="esim. Ilmamäärä"
            testId={`edit-measurement-name-${m.id}`}
          />
          <div className="grid grid-cols-[1fr_100px] gap-2">
            <MeasField
              label="Tulos"
              value={m.value || ""}
              onSave={(v) => updateEntry(m.id, { value: v })}
              placeholder="esim. 42.5"
              testId={`edit-measurement-value-${m.id}`}
              inputMode="decimal"
            />
            <MeasField
              label="Yksikkö"
              value={m.unit || ""}
              onSave={(v) => updateEntry(m.id, { unit: v })}
              placeholder="l/s"
              testId={`edit-measurement-unit-${m.id}`}
            />
          </div>
        </div>
      ))}

      {adding && (
        <div className="rounded-2xl bg-emerald-50/40 border border-emerald-200 p-3.5 space-y-2.5" data-testid="edit-measurement-new-form">
          <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold">Uusi mittaus</div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Mittaus</label>
            <input
              autoFocus
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="esim. Ilmamäärä"
              list="meas-name-suggestions"
              className="w-full px-3 py-3 rounded-xl border border-slate-200 bg-white text-base"
              data-testid="edit-measurement-new-name"
            />
            <datalist id="meas-name-suggestions">
              {MEAS_NAME_SUGGESTIONS.map((s) => <option key={s} value={s} />)}
            </datalist>
            <div className="mt-1.5 flex flex-wrap gap-1">
              {MEAS_NAME_SUGGESTIONS.slice(0, 5).map((s) => (
                <button
                  key={s} type="button"
                  onClick={() => setName(s)}
                  className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${name === s ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-white border-slate-200 text-slate-600"}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-[1fr_100px] gap-2">
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Tulos</label>
              <input
                type="text"
                value={value}
                inputMode="decimal"
                onChange={(e) => setValue(e.target.value)}
                placeholder="42.5"
                className="w-full px-3 py-3 rounded-xl border border-slate-200 bg-white text-base"
                data-testid="edit-measurement-new-value"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Yksikkö</label>
              <input
                type="text"
                value={unitStr}
                onChange={(e) => setUnitStr(e.target.value)}
                placeholder="l/s"
                list="meas-unit-suggestions"
                className="w-full px-3 py-3 rounded-xl border border-slate-200 bg-white text-base"
                data-testid="edit-measurement-new-unit"
              />
              <datalist id="meas-unit-suggestions">
                {MEAS_UNIT_SUGGESTIONS.map((s) => <option key={s} value={s} />)}
              </datalist>
            </div>
          </div>
          <div className="flex flex-wrap gap-1">
            {MEAS_UNIT_SUGGESTIONS.slice(0, 8).map((u) => (
              <button
                key={u} type="button"
                onClick={() => setUnitStr(u)}
                className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${unitStr === u ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-white border-slate-200 text-slate-600"}`}
              >
                {u}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={() => { setAdding(false); setName(""); setValue(""); setUnitStr(""); }}
              className="flex-1 px-3 py-2.5 rounded-lg border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50"
              data-testid="edit-measurement-cancel"
            >
              Peruuta
            </button>
            <button
              onClick={commit}
              disabled={!name.trim() && !value.trim()}
              className="flex-1 px-3 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50"
              data-testid="edit-measurement-save"
            >
              Tallenna mittaus
            </button>
          </div>
        </div>
      )}

      {!adding && (
        <button
          onClick={() => setAdding(true)}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl border-2 border-dashed border-emerald-300 text-emerald-700 font-semibold hover:bg-emerald-50 text-base"
          data-testid="edit-measurement-add"
        >
          <Plus className="h-4 w-4" /> Lisää mittaus
        </button>
      )}
    </div>
  );
}

function MeasField({ label, value, onSave, placeholder, testId, inputMode }) {
  const [val, setVal] = useState(value);
  useEffect(() => setVal(value), [value]);
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">{label}</label>
      <input
        type="text"
        inputMode={inputMode}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={() => { if (val !== value) onSave(val); }}
        placeholder={placeholder}
        className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-base"
        data-testid={testId}
      />
    </div>
  );
}

function Level3Devices({ unit, onPatch }) {
  const [adding, setAdding] = useState(false);
  const [brandModel, setBrandModel] = useState("");
  const [serialId, setSerialId] = useState("");
  const [location, setLocation] = useState("");
  const devices = unit.devices || [];

  const commit = () => {
    const bm = (brandModel || "").trim();
    const sid = (serialId || "").trim();
    const loc = (location || "").trim();
    if (!bm && !sid && !loc) {
      setAdding(false);
      return;
    }
    const newDevice = {
      id: Math.random().toString(36).slice(2, 12),
      brand_model: bm,
      serial_id: sid,
      location: loc,
      extra: {},
    };
    onPatch({ devices: [...devices, newDevice] });
    setBrandModel("");
    setSerialId("");
    setLocation("");
    setAdding(false);
  };

  const remove = (id) => {
    onPatch({ devices: devices.filter((d) => d.id !== id) });
  };

  const updateDevice = (id, patch) => {
    onPatch({
      devices: devices.map((d) => (d.id === id ? { ...d, ...patch } : d)),
    });
  };

  return (
    <div className="space-y-3" data-testid="edit-level-3-devices">
      {devices.length === 0 && !adding && (
        <div className="rounded-2xl bg-white border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500">
          Ei laitteita tallennettuna.
        </div>
      )}

      {devices.map((d, i) => (
        <div
          key={d.id}
          className="rounded-2xl bg-white border border-slate-200 p-3.5 space-y-2.5"
          data-testid={`edit-device-${d.id}`}
        >
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Laite {i + 1}</div>
            <button
              onClick={() => remove(d.id)}
              className="text-slate-300 hover:text-red-600 p-1"
              data-testid={`edit-device-remove-${d.id}`}
              aria-label="Poista laite"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <DeviceField
            label="Merkki ja malli"
            value={d.brand_model || ""}
            onSave={(v) => updateDevice(d.id, { brand_model: v })}
            placeholder="esim. FläktGroup eQ Master"
            testId={`edit-device-brand-${d.id}`}
          />
          <DeviceField
            label="Sarjanumero / Tunniste"
            value={d.serial_id || ""}
            onSave={(v) => updateDevice(d.id, { serial_id: v })}
            placeholder="esim. TK-1"
            testId={`edit-device-serial-${d.id}`}
          />
          <DeviceField
            label="Sijainti (valinnainen)"
            value={d.location || ""}
            onSave={(v) => updateDevice(d.id, { location: v })}
            placeholder="esim. Tekninen tila, 2. krs"
            testId={`edit-device-location-${d.id}`}
          />
        </div>
      ))}

      {adding && (
        <div className="rounded-2xl bg-emerald-50/40 border border-emerald-200 p-3.5 space-y-2.5" data-testid="edit-device-new-form">
          <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold">Uusi laite</div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Merkki ja malli</label>
            <input
              autoFocus
              type="text"
              value={brandModel}
              onChange={(e) => setBrandModel(e.target.value)}
              placeholder="esim. FläktGroup eQ Master"
              className="w-full px-3 py-3 rounded-xl border border-slate-200 bg-white text-base"
              data-testid="edit-device-new-brand"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Sarjanumero / Tunniste</label>
            <input
              type="text"
              value={serialId}
              onChange={(e) => setSerialId(e.target.value)}
              placeholder="esim. TK-1"
              className="w-full px-3 py-3 rounded-xl border border-slate-200 bg-white text-base"
              data-testid="edit-device-new-serial"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Sijainti (valinnainen)</label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="esim. Tekninen tila, 2. krs"
              className="w-full px-3 py-3 rounded-xl border border-slate-200 bg-white text-base"
              data-testid="edit-device-new-location"
            />
          </div>
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={() => { setAdding(false); setBrandModel(""); setSerialId(""); setLocation(""); }}
              className="flex-1 px-3 py-2.5 rounded-lg border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50"
              data-testid="edit-device-cancel"
            >
              Peruuta
            </button>
            <button
              onClick={commit}
              disabled={!brandModel.trim() && !serialId.trim() && !location.trim()}
              className="flex-1 px-3 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50"
              data-testid="edit-device-save"
            >
              Tallenna laite
            </button>
          </div>
        </div>
      )}

      {!adding && (
        <button
          onClick={() => setAdding(true)}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl border-2 border-dashed border-emerald-300 text-emerald-700 font-semibold hover:bg-emerald-50 text-base"
          data-testid="edit-device-add"
        >
          <Plus className="h-4 w-4" /> Lisää laite
        </button>
      )}
    </div>
  );
}

function DeviceField({ label, value, onSave, placeholder, testId }) {
  const [val, setVal] = useState(value);
  useEffect(() => setVal(value), [value]);
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">{label}</label>
      <input
        type="text"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={() => { if (val !== value) onSave(val); }}
        placeholder={placeholder}
        className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-base"
        data-testid={testId}
      />
    </div>
  );
}


function Level3Materials({ unit, savedMaterials, onPatch }) {
  const materials = unit.materials_used || [];
  const updateMaterials = (next) => onPatch({ materials_used: next });
  return (
    <div data-testid="edit-level-3-materials">
      <MaterialsEditor
        materials={materials}
        savedMaterials={savedMaterials}
        onChange={updateMaterials}
        unitId={unit.id}
      />
    </div>
  );
}

function SummaryCard({ report, editToken, onSaved }) {
  const [text, setText] = useState(report.ai_summary || "");
  const [saving, setSaving] = useState(false);
  const initialRef = useRef(report.ai_summary || "");
  useEffect(() => {
    setText(report.ai_summary || "");
    initialRef.current = report.ai_summary || "";
  }, [report.ai_summary]);

  const save = async () => {
    if (text === initialRef.current) return;
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/reports/edit/${editToken}/summary`, { ai_summary: text });
      initialRef.current = text;
      onSaved?.(data);
    } catch (e) {
      toast.error("Yhteenvedon tallennus epäonnistui");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-slate-100 p-4" data-testid="edit-summary-card">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold">Tehdyt toimenpiteet / Lisätiedot</div>
          <div className="text-xs text-slate-500">Voit muokata koko kohteen yhteenvetoa</div>
        </div>
        {saving && <Loader2 className="h-4 w-4 text-slate-400 animate-spin" />}
      </div>
      <textarea
        rows={8}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={save}
        placeholder="Kirjoita yhteenveto koko kohteen työstä…"
        className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50/30 text-sm leading-relaxed"
        data-testid="edit-summary-textarea"
      />
    </div>
  );
}


/* ============ Level 3: Muut kerättävät tiedot (custom fields) ============ */
function Level3CustomFields({ unit, report, onPatch }) {
  const defs = report?.custom_fields || [];
  const values = unit.custom_values || {};

  const setValue = (fieldId, value) => {
    onPatch({ custom_values: { ...values, [fieldId]: value } });
  };

  if (defs.length === 0) {
    return (
      <div className="rounded-2xl bg-white border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500" data-testid="edit-level-3-custom-empty">
        Ei kenttiä määritelty. Ota yhteyttä toimistoon jos kenttien pitäisi näkyä.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="edit-level-3-custom">
      {defs.map((def) => (
        <CustomFieldInput
          key={def.id}
          def={def}
          value={values[def.id] || ""}
          onSave={(v) => setValue(def.id, v)}
        />
      ))}
    </div>
  );
}

function CustomFieldInput({ def, value, onSave }) {
  const [val, setVal] = useState(value);
  useEffect(() => setVal(value), [value]);
  const commit = () => { if (val !== value) onSave(val); };

  const label = def.label || "Kenttä";
  const hint = def.hint || "";
  const testId = `edit-custom-${def.id}`;

  if (def.type === "select") {
    return (
      <div className="rounded-2xl bg-white border border-slate-200 p-3.5 space-y-2" data-testid={testId}>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
          {hint && <div className="text-xs text-slate-400 mt-0.5">{hint}</div>}
        </div>
        <div className="flex flex-wrap gap-2">
          {(def.options || []).map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => { setVal(opt); onSave(opt); }}
              className={`px-3 py-2 rounded-xl text-sm font-semibold border transition-colors ${
                val === opt
                  ? "bg-emerald-600 text-white border-emerald-600"
                  : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
              }`}
              data-testid={`${testId}-opt-${opt}`}
            >
              {opt}
            </button>
          ))}
          {val && (
            <button
              type="button"
              onClick={() => { setVal(""); onSave(""); }}
              className="px-2 py-2 rounded-xl text-xs font-semibold text-slate-400 hover:text-red-600"
              data-testid={`${testId}-clear`}
              aria-label="Tyhjennä valinta"
            >
              Tyhjennä
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-3.5 space-y-2" data-testid={testId}>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
        {hint && <div className="text-xs text-slate-400 mt-0.5">{hint}</div>}
      </div>
      <input
        type={def.type === "number" ? "text" : "text"}
        inputMode={def.type === "number" ? "decimal" : undefined}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        placeholder={def.type === "number" ? "esim. 12500" : ""}
        className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-base"
        data-testid={`${testId}-input`}
      />
    </div>
  );
}
